import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDrop } from 'react-dnd';

import './DropTarget.scss';

const DIRECTION_VERTICAL = 'vertical';
const DIRECTION_HORIZONTAL = 'horizontal';

const propTypes = {

	/**
	 * The drag-and-drop type of the drop target.
	 */
	type: PropTypes.string.isRequired,

	/**
	 * The direction of the drop target.
	 */
	direction: PropTypes.oneOf([DIRECTION_VERTICAL, DIRECTION_HORIZONTAL]),

	/**
	 * The current item related to this drop target.
	 */
	item: PropTypes.shape({

		/**
		 * The unique ID of the item.
		 */
		id: PropTypes.string,

	}),

	/**
	 * The previous item (if any) related to this drop target.
	 */
	prevItem: PropTypes.shape({

		/**
		 * The unique ID of the item.
		 */
		id: PropTypes.string,

	}),

	/**
	 * The next item (if any) related to this drop target.
	 */
	nextItem: PropTypes.shape({

		/**
		 * The unique ID of the item.
		 */
		id: PropTypes.string,

	}),

	/**
	 * The item content to render.
	 */
	children: PropTypes.node,

};

const defaultProps = {
	direction: 'vertical',
};

/**
 * Shared component to render a basic Drop Target, using react-dnd.
 *
 * @param   {Object} props  The props.
 * @returns {Object}        The rendered component.
 */
const DropTarget = ({ type, direction, item, prevItem, nextItem, children }) => {

	const targetRef = useRef();

	// Keep track of the current hover status for each possible location for this drop target
	const [hoverStatus, setHoverStatus] = useState({
		top: false,
		right: false,
		bottom: false,
		left: false,
	});

	const [collectedDndProps, connectDropTarget] = useDrop({
		accept: type,
		drop: () => {

			// Check if dropped to the left
			if (hoverStatus.left) {

				// Place the dragged item b/w the previous item and the current item
				return {
					prevItem,
					nextItem: item,
				};

			// Check if dropped to the right
			} else if (hoverStatus.right) {

				// Place the dragged item b/w the current item and the next item
				return {
					prevItem: item,
					nextItem,
				};

			// Check if dropped above
			} else if (hoverStatus.top) {

				// Place the dragged item b/w the previous item and the current item
				return {
					prevItem,
					nextItem: item,
				};

			// Check if dropped below
			} else if (hoverStatus.bottom) {

				// Place the dragged item b/w the current item and the next item
				return {
					prevItem: item,
					nextItem,
				};

			}

			// Otherwise, don't do anything :)
			return undefined;

		},
		hover: (draggedItem, monitor) => {

			// Calculate which of the drop target locations are valid (if any)
			const validLocations = validTargetLocations(
				direction,
				draggedItem,
				item,
				prevItem,
				nextItem,
			);

			// Re-calculate the new hover status for each location of this drop target
			const updatedHoverStatus = calculateHoverLocation(targetRef, monitor, validLocations);

			// Force the component to re-render to indicate the updated hover status
			setHoverStatus(updatedHoverStatus);

		},
		collect: (monitor) => {
			return {
				isOver: monitor.isOver(),
			};

		},
	});

	const isOver = collectedDndProps.isOver;

	return (
		<div
			ref={connectDropTarget(targetRef)}
			className="tc-drop-target">
			{children}
			{hoverStatus.top && isOver &&
				<div className="tc-drop-target-location tc-drop-target-hover-top">
				</div>
			}
			{hoverStatus.right && isOver &&
				<div className="tc-drop-target-location tc-drop-target-hover-right">
				</div>
			}
			{hoverStatus.bottom && isOver &&
				<div className="tc-drop-target-location tc-drop-target-hover-bottom">
				</div>
			}
			{hoverStatus.left && isOver &&
				<div className="tc-drop-target-location tc-drop-target-hover-left">
				</div>
			}
		</div>
	);

};

DropTarget.propTypes = propTypes;
DropTarget.defaultProps = defaultProps;

export default DropTarget;

/**
 * Calculate the hover status for each location of this drop target to determine if any are active.
 *
 * @param {object} node                 The React node (via Ref) for the drop target.
 * @param {object} monitor              The current drag-and-drop state, provided by react-dnd.
 *
 * @param {object} validLocations       The valid locations for this drop target given the
 *                                      item being dragged.
 * @param {bool} validLocations.top     True, if the top side is a valid drop location.
 * @param {bool} validLocations.right   True, if the right side is a valid drop location.
 * @param {bool} validLocations.bottom  True, if the bottom side is a valid drop location.
 * @param {bool} validLocations.left    True, if the left side is a valid drop location.
 *
 * @returns {object} result       The hover status result.
 * @returns {bool} result.top     True, if the top side is a valid location and is hovered over.
 * @returns {bool} result.right   True, if the right side is a valid location and is hovered over.
 * @returns {bool} result.bottom  True, if the bottom side is a valid location and is hovered over.
 * @returns {bool} result.left    True, if the left side is a valid location and is hovered over.
 */
function calculateHoverLocation(node, monitor, validLocations) {

	const result = {
		top: false,
		right: false,
		bottom: false,
		left: false,
	};

	// Determine the drop target xy coordinates on screen
	const hoverBoundingRect = node.current.getBoundingClientRect();

	// Determine the mouse position xy coordinates on screen
	const clientOffset = monitor.getClientOffset();

	// Determine the width and height of the drop target
	const width = hoverBoundingRect.right - hoverBoundingRect.left;
	const height = hoverBoundingRect.bottom - hoverBoundingRect.top;

	// Determine the distance from the mouse position to the drop target locations
	const distToTop = clientOffset.y - hoverBoundingRect.top;
	const distToLeft = clientOffset.x - hoverBoundingRect.left;
	const distToRight = hoverBoundingRect.right - clientOffset.x;
	const distToBottom = hoverBoundingRect.bottom - clientOffset.y;

	const validOptions = [];

	if (validLocations.top) {
		validOptions.push(distToTop);
	}

	if (validLocations.right) {
		validOptions.push(distToRight);
	}

	if (validLocations.bottom) {
		validOptions.push(distToBottom);
	}

	if (validLocations.left) {
		validOptions.push(distToLeft);
	}

	// Pick the closest of the valid drop target locations
	const min = Math.min(...validOptions);

	// Determine which drop target location is active (if any)
	if (min === distToTop && validLocations.top && distToTop < height / 2) {

		result.top = true;

	} else if (min === distToRight && validLocations.right && distToRight < width / 2) {

		result.right = true;

	} else if (min === distToBottom && validLocations.bottom && distToBottom < height / 2) {

		result.bottom = true;

	} else if (min === distToLeft && validLocations.left && distToLeft < width / 2) {

		result.left = true;

	}

	return result;

}

/**
 * Calculates the valid locations for this drop target given the item being dragged.
 *
 * @param {string} direction    The drag-and-drop direction to use.
 * @param {object} draggedItem  The item being dragged, provided by react-dnd.
 * @param {object} item         The item related to this drop target.
 * @param {object} prevItem     The item that comes before this drop target.
 * @param {object} nextItem     The item that goes after this drop target.
 *
 * @returns {object} result       The valid locations for this drop target given
 *                                the item being dragged.
 * @returns {bool} result.top     True, if the top side is a valid drop location.
 * @returns {bool} result.right   True, if the right side is a valid drop location.
 * @returns {bool} result.bottom  True, if the bottom side is a valid drop location.
 * @returns {bool} result.left    True, if the left side is a valid drop location.
 */
function validTargetLocations(direction, draggedItem, item, prevItem, nextItem) {

	const result = {
		top: false,
		right: false,
		bottom: false,
		left: false,
	};

	// Check to make sure that the dragged item is not the current item
	if (!draggedItem || !draggedItem.data || draggedItem.data.id === item.id) {
		return result;
	}

	const isPrevious = prevItem && prevItem.id === draggedItem.data.id;
	const isNext = nextItem && nextItem.id === draggedItem.data.id;

	if (direction === DIRECTION_VERTICAL) {

		// Make sure the previous item isn't the dragged item itself
		if (!isPrevious) {
			result.top = true;
		}

		// Make sure the next item isn't the dragged item itself
		if (!isNext) {
			result.bottom = true;
		}

	} else if (direction === DIRECTION_HORIZONTAL) {

		// Make sure the previous item isn't the dragged item itself
		if (!isPrevious) {
			result.left = true;
		}

		// Make sure the next item isn't the dragged item itself
		if (!isNext) {
			result.right = true;
		}

	}

	return result;

}
